import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CalendarDays, Copy, Download, Loader2, MessageCircle, Facebook, Music2, CheckCircle2, RefreshCw, Clock, Repeat,
} from "lucide-react";
import { api, apiError, BACKEND_URL, downloadFile } from "../../lib/api";

const PLATFORM = {
  whatsapp: { label: "WhatsApp", icon: MessageCircle, cls: "bg-green-600/20 text-green-300 border-green-500/40" },
  facebook: { label: "Facebook", icon: Facebook, cls: "bg-blue-600/20 text-blue-300 border-blue-500/40" },
  tiktok: { label: "TikTok", icon: Music2, cls: "bg-white/10 text-zinc-200 border-white/30" },
};

const fmtDate = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });
};

const PostCard = ({ p, onPublished }) => {
  const plat = PLATFORM[p.platform] || PLATFORM.facebook;
  const fullCaption = (p.caption || "") + (p.link ? `\n\n👉 ${p.link}` : "");

  const copyText = async () => {
    await navigator.clipboard.writeText(fullCaption);
    toast.success("Texto copiado (incluye tu link personal)");
  };

  const publish = async () => {
    if (p.platform === "whatsapp") {
      if (p.image_url) downloadFile(`${BACKEND_URL}${p.image_url}`, `renalife-dia${p.day}.jpg`);
      window.open(`https://wa.me/?text=${encodeURIComponent(fullCaption)}`, "_blank");
      toast.success("WhatsApp abierto con tu texto. Adjunta la imagen descargada y envía.");
    } else {
      try { await navigator.clipboard.writeText(fullCaption); } catch (err) { console.warn("clipboard:", err); }
      if (p.platform === "tiktok" && p.video_url) downloadFile(p.video_url, `renalife-dia${p.day}.mp4`);
      else if (p.image_url) downloadFile(`${BACKEND_URL}${p.image_url}`, `renalife-dia${p.day}.jpg`);
      window.open(p.platform === "tiktok" ? "https://www.tiktok.com/upload" : "https://www.facebook.com/", "_blank");
      toast.success("Texto copiado y archivo descargado. Pega, sube y publica.");
    }
    try { await api.post(`/renalife/posts/${p.post_id}/published`); onPublished(p.post_id); } catch (err) { console.warn(err); }
  };

  return (
    <div data-testid={`calendar-post-${p.post_id}`} className={`rounded-xl border p-4 ${p.status === "published" ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-black/30"}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full border px-2.5 py-1 ${plat.cls}`}>
          <plat.icon size={12} /> {plat.label}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] rounded-full border border-[#FACC15]/40 bg-[#FACC15]/10 text-[#FACC15] px-2.5 py-1 font-semibold">
          <Clock size={11} /> {p.slot}
        </span>
        {p.is_repost && (
          <span className="inline-flex items-center gap-1 text-[11px] rounded-full border border-purple-400/40 bg-purple-500/10 text-purple-300 px-2.5 py-1">
            <Repeat size={11} /> Repost con variación
          </span>
        )}
        {p.status === "published" && (
          <span data-testid="post-published-badge" className="inline-flex items-center gap-1 text-[11px] text-emerald-400 font-bold ml-auto"><CheckCircle2 size={13} /> Publicada</span>
        )}
      </div>
      <div className="text-xs font-mono text-emerald-200/70 mb-2">{p.material_title}</div>
      {p.status === "pending" || p.status === "processing" ? (
        <div className="flex items-center gap-2 text-sm text-zinc-400 py-4"><Loader2 size={15} className="animate-spin text-[#FACC15]" /> Preparando esta publicación…</div>
      ) : p.status === "error" ? (
        <div className="text-xs text-red-400 py-2">No se pudo generar. Usa "Reanudar generación" arriba para reintentar.</div>
      ) : (
        <div className="grid sm:grid-cols-[140px_1fr] gap-4">
          {p.image_url ? (
            <img src={`${BACKEND_URL}${p.image_url}`} alt={p.material_title} className="rounded-lg border border-white/10 w-full sm:w-[140px] object-cover" />
          ) : <div />}
          <div>
            <p className="text-[13px] text-zinc-200 whitespace-pre-wrap leading-relaxed">{p.caption}</p>
            {p.video_url && <video src={p.video_url} controls className="mt-2 rounded-lg max-h-44 bg-black" />}
            <div className="flex flex-wrap gap-2 mt-3">
              <button data-testid={`copy-post-${p.post_id}`} onClick={copyText} className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 border border-white/15 text-zinc-300 hover:bg-white/5"><Copy size={12} /> Copiar texto</button>
              {p.image_url && (
                <button onClick={() => downloadFile(`${BACKEND_URL}${p.image_url}`, `renalife-dia${p.day}.jpg`)} className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 border border-white/15 text-zinc-300 hover:bg-white/5"><Download size={12} /> Imagen</button>
              )}
              <button data-testid={`publish-post-${p.post_id}`} onClick={publish} className="inline-flex items-center gap-1 text-xs font-bold rounded-lg px-4 py-1.5 bg-[#FACC15] text-[#022C22] hover:bg-yellow-300">
                Publicar con 1 toque
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RULES = [
  "1 publicación principal al día (días 6 a 10 agregamos un repost con foto y texto DIFERENTES, separado 10+ horas).",
  "Horarios de alta actividad: entre semana 8:00–9:00 a.m. o 6:00–9:00 p.m.; fines de semana 10:00 a.m.–12:00 p.m.",
  "Nada de clones: cada pieza cambia foto, título (LO NUEVO, LA TENDENCIA, ¿YA LO ESCUCHASTE?) y descripción.",
  "Nunca publiques varias piezas iguales en minutos: eso se interpreta como spam.",
];

export default function RenalifeCalendar() {
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/renalife/schedule");
      setData(d);
      const hasPending = (d.posts || []).some((p) => ["pending", "processing"].includes(p.status));
      if (hasPending) {
        clearTimeout(timer.current);
        timer.current = setTimeout(load, 6000);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
    return () => clearTimeout(timer.current);
  }, [load]);

  const start = async () => {
    setStarting(true);
    try {
      const { data: d } = await api.post("/renalife/schedule/start");
      setData(d);
      toast.success("¡Calendario iniciado! La IA está preparando tus publicaciones con variaciones.");
      clearTimeout(timer.current);
      timer.current = setTimeout(load, 6000);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  const resume = async () => {
    try {
      await api.post("/renalife/schedule/resume");
      toast.success("Reanudando la generación…");
      clearTimeout(timer.current);
      timer.current = setTimeout(load, 5000);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const markPublished = (postId) => {
    setData((d) => ({ ...d, posts: d.posts.map((p) => (p.post_id === postId ? { ...p, status: "published" } : p)) }));
  };

  if (!data) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FACC15]" size={30} /></div>;
  }

  const sched = data.schedule;
  const posts = data.posts || [];
  const days = [...new Set(posts.map((p) => p.day))];
  const generating = posts.some((p) => ["pending", "processing"].includes(p.status));
  const errors = posts.some((p) => p.status === "error");

  return (
    <div data-testid="renalife-calendar-page" className="space-y-8">
      <div>
        <h1 className="font-renalife font-bold text-3xl sm:text-4xl text-[#F8FAF8]">
          Calendario de publicaciones <span className="text-[#FACC15]">15 días</span>
        </h1>
        <p className="text-sm text-emerald-100/70 mt-2 max-w-2xl">
          La IA prepara tus publicaciones para WhatsApp, Facebook y TikTok con VARIACIONES reales (foto nueva, título
          con otro ángulo y descripción reescrita) para que publiques 15 días seguidos sin parecer spam.
        </p>
      </div>

      <div className="rounded-2xl border border-[#FACC15]/25 p-6" style={{ background: "linear-gradient(135deg, #022C22, #064E3B)" }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FACC15]/70 mb-3">La estrategia segura</div>
        <ul className="space-y-2">
          {RULES.map((r) => (
            <li key={r} className="flex items-start gap-2 text-[13px] text-emerald-100/85">
              <CheckCircle2 size={15} className="text-[#FACC15] mt-0.5 shrink-0" /> {r}
            </li>
          ))}
        </ul>
      </div>

      {!sched ? (
        <div className="text-center py-10">
          <button
            data-testid="start-schedule-btn"
            onClick={start}
            disabled={starting}
            className="inline-flex items-center gap-3 rounded-full px-10 py-5 font-renalife font-bold text-xl bg-[#FACC15] text-[#022C22] hover:bg-yellow-300 transition-transform hover:-translate-y-0.5 disabled:opacity-60"
          >
            {starting ? <Loader2 size={22} className="animate-spin" /> : <CalendarDays size={22} />}
            EMPEZAR PUBLICACIONES
          </button>
          <p className="text-xs text-zinc-500 mt-4">
            Al presionar, la IA genera las 20 piezas de tus 15 días (toma unos minutos). Tu calendario empieza mañana.
          </p>
        </div>
      ) : (
        <>
          <div data-testid="schedule-progress" className="rounded-xl border border-white/10 bg-black/30 p-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs text-zinc-400 mb-1.5">
                {generating ? "La IA está preparando tus publicaciones…" : "Publicaciones listas"} · {sched.ready}/{sched.total}
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-[#FACC15] transition-all" style={{ width: `${(sched.ready / Math.max(sched.total, 1)) * 100}%` }} />
              </div>
            </div>
            {(generating || errors) && (
              <button data-testid="resume-schedule-btn" onClick={resume} className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2 border border-[#FACC15]/40 text-[#FACC15] hover:bg-[#FACC15]/10">
                <RefreshCw size={13} /> ¿Se detuvo? Reanudar generación
              </button>
            )}
          </div>

          <div className="space-y-6">
            {days.map((day) => {
              const dayPosts = posts.filter((p) => p.day === day);
              return (
                <div key={day} data-testid={`calendar-day-${day}`}>
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="font-renalife font-bold text-lg text-[#FACC15]">Día {day}</span>
                    <span className="text-xs text-zinc-400 capitalize">{fmtDate(dayPosts[0].date)}</span>
                  </div>
                  <div className="space-y-3">
                    {dayPosts.map((p) => <PostCard key={p.post_id} p={p} onPublished={markPublished} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
